import {
  getDatasetsInItems,
  getReturnIdsFromDatasetInstruction,
  normalizeDatasetId,
  serializeTableAccessor,
} from 'helpers/dataset.helper/dataset.helper'
import { clone, getTypeObject, sortArrayOfDicts } from 'helpers/global.helper/global.helper'
import i18n from 'i18next'
import React, { ReactElement, useCallback, useDeferredValue, useMemo, useRef, useState } from 'react'
import { AutoSizer } from 'react-virtualized-auto-sizer'
import { Dialog } from 'ui/components/Dialog/Dialog'
import TimeSeriesDataTable from 'ui/components/TimeSeriesDataTable/TimeSeriesDataTable'
import { TimeSeriesDataTableColumnDef, TimeSeriesDataTableData } from 'ui/components/TimeSeriesDataTable/types'
import {
  registerAppendPropsWithDatasets,
  registerComponentHeight,
  registerDefaultProps,
  registerUiConfigComponent,
} from 'ui/uiConfig/factory'
import { addLocaleSeparators } from 'utils/numberUtils/numberUtils'
import { useSnapshot } from 'valtio'
import optimizeViewStore from 'views/OptimizeView/store/optimizeViewStore'
import { ChartItem } from '../Chart/Chart.types'
import { sortChartItems } from '../Chart/Tooltip/tooltip.helper'
import {
  cleanDatasetsForTable,
  getFooterAggregationRows,
  getHourlyDataForTable,
  getQuarterlyDataForTable,
} from './DatasetTable.helper'
import styles from './DatasetTable.module.less'
import { DatasetTableItem, DatasetTableProps } from './DatasetTable.types'
import DatasetTableHeader from './DatasetTableHeader'

const defaultProps: Omit<DatasetTableProps, `datasets` | `children`> = {
  items: [
    {
      title: ``,
      sub_title: '',
      data_id: ``,
      unit: ``,
      table_order: 0,
      decimals: 1,
      data_type: ``,
      border_right: false,
    },
  ],
  title: ``,
  do_not_show_zero_or_null_values_in_table: false,
  do_not_show_download_button: false,
  do_not_show_sum_or_average_row: false,
  do_not_show_min_max_row: false,
  show_only_value_in_future: false,
  do_not_show_top_toolbar: false,
}

function appendPropsWithDataset(
  datasetInstruction: DatasetInstruction,
  previousProps: DatasetTableProps
): DatasetTableProps {
  const returnIds = getReturnIdsFromDatasetInstruction(datasetInstruction)
  const newItems = returnIds.map((returnId) => ({
    title: returnId,
    sub_title: '',
    data_id: returnId,
    unit: ``,
    decimals: 1,
    data_type: '' as DatasetTableItem['data_type'],
    table_order: 0,
    border_right: false,
  }))

  return {
    ...previousProps,
    do_not_show_zero_or_null_values_in_table: false,
    do_not_show_download_button: false,
    do_not_show_sum_or_average_row: false,
    do_not_show_min_max_row: false,
    show_only_value_in_future: false,
    show_initial_page_with_current_optimization_hour: false,
    show_initial_page_with_current_day: false,
    initial_page_size: 10,
    do_not_show_top_toolbar: false,
    use_column_color_from_items: false,
    footer_calculates_per_page: false,
    sorting_function: 'table_order',
    title: ``,
    items: [...(previousProps?.items || []), ...newItems],
  }
}

registerUiConfigComponent('dataset_table', DatasetTable)
registerAppendPropsWithDatasets('dataset_table', appendPropsWithDataset)
registerDefaultProps('dataset_table', defaultProps)
registerComponentHeight('dataset_table', 300)

function sortDatasetItems(items: DatasetTableItem[], sorting_function?: string): DatasetTableItem[] {
  if (sorting_function === 'table_order') {
    return sortArrayOfDicts(items, 'table_order')
  } else if (sorting_function === 'order') {
    return sortArrayOfDicts(items, 'order')
  } else if (sorting_function === 'chart_items') {
    //same function that sorts chart items in charts and tooltip
    return sortChartItems(items)
  } else if (sorting_function === 'alphabetical') {
    return items.sort((a, b) => a.title.localeCompare(b.title))
  } else if (sorting_function === 'chart_table_order') {
    //sorts first by chart items, then in table order including undefined values
    const chartOrder = sortChartItems(items) as DatasetTableItem[]
    return sortTableOrderWithUndefined(chartOrder)
  }
  return items
}

// sort that allows table_order undefined, items without table_order will be put first, then items with table_order are sorted numerically and ascending
function sortTableOrderWithUndefined(items: DatasetTableItem[]): DatasetTableItem[] {
  return items.sort((a, b) => {
    if (a.table_order === b.table_order) {
      return 0
    } else if (!a.table_order) {
      return -1
    } else if (!b.table_order) {
      return 1
    }
    return a.table_order > b.table_order ? 1 : -1
  })
}

function getColumnTitleFromItem(item: DatasetTableItem): string {
  if (item.title && item.sub_title) {
    return `${item.title} ${item.sub_title}`
  }

  return item.title
}

function LocaleSeparatorCell({ cell }: { cell: { getValue: () => unknown } }) {
  const value = cell.getValue() as number | string | null
  return value == null ? value : addLocaleSeparators(value)
}

export type DatasetTableComponentProps = DatasetTableProps & { uid?: number }

type DatasetTableInnerProps = DatasetTableComponentProps & {
  onFullScreenClick?: () => void
}

// Opens an in-place full screen copy of this table inside a dialog. The copy renders
// the same live props, so it inherits filtering, grouping, zoom and refresh behaviour
// from the parent and updates as the data refetches.
export default function DatasetTable(props: DatasetTableComponentProps): ReactElement {
  const [isFullScreen, setIsFullScreen] = useState(false)
  const paginationRef = useRef<{ pageIndex: number; pageSize: number } | null>(null)

  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
    paginationRef.current = newPagination
    props.onPaginationChange?.(newPagination)
  }

  return (
    <>
      <DatasetTableInner
        {...props}
        onPaginationChange={handlePaginationChange}
        onFullScreenClick={() => setIsFullScreen(true)}
      />
      {isFullScreen && (
        <Dialog open fullScreen onClose={() => setIsFullScreen(false)}>
          <div className="h-full box-border overflow-hidden">
            <DatasetTableInner
              {...props}
              onPaginationChange={handlePaginationChange}
              initial_page_index={paginationRef.current?.pageIndex}
              fillParentHeight
              showCloseButton
              onCloseClick={() => setIsFullScreen(false)}
            />
          </div>
        </Dialog>
      )}
    </>
  )
}

function DatasetTableInner({
  items,
  customColumns,
  datasets,
  title,
  do_not_show_zero_or_null_values_in_table,
  do_not_show_download_button,
  do_not_show_sum_or_average_row,
  do_not_show_min_max_row,
  show_only_value_in_future,
  show_initial_page_with_current_optimization_hour,
  show_initial_page_with_current_day,
  initial_page_size,
  initial_page_index,
  do_not_show_top_toolbar,
  columnOverrides,
  enableEditing,
  enableColumnActions,
  tableKey,
  disableCustomSorting,
  tableContainerRef,
  use_column_color_from_items,
  footer_calculates_per_page,
  sorting_function,
  onMouseMove,
  max_height,
  sticky_table_header,
  onPaginationChange,
  hideSingleValueDatasets = true,
  expandableHours,
  enableCellRangeSelectionForClipboard,
  showCloseButton,
  onCloseClick,
  fillParentHeight,
  onFullScreenClick,
}: DatasetTableInnerProps): ReactElement {
  const optimizeViewSnap = useSnapshot(optimizeViewStore)

  // Defer heavy data computations to lower priority renders
  const normalizedDatasets = useDeferredValue(datasets || [])
  const normalizedItems = useDeferredValue(items || [])

  const itemsInOrder = useMemo(
    () => sortDatasetItems(clone(normalizedItems), sorting_function),
    [normalizedItems, sorting_function]
  )

  const datasetNames = useMemo(
    () => (itemsInOrder || []).reduce((acc, item) => ({ ...acc, [item.data_id]: getColumnTitleFromItem(item) }), {}),
    [itemsInOrder]
  )

  const datasetsInExport = useMemo(
    () => getDatasetsInItems(itemsInOrder as ChartItem[], clone(normalizedDatasets)),
    [itemsInOrder, normalizedDatasets]
  )

  const datasetsInTable = useMemo(
    () =>
      !do_not_show_sum_or_average_row || !do_not_show_min_max_row
        ? getDatasetsInItems(
            itemsInOrder as ChartItem[],
            cleanDatasetsForTable(
              normalizedDatasets,
              itemsInOrder,
              do_not_show_zero_or_null_values_in_table,
              hideSingleValueDatasets
            )
          )
        : cleanDatasetsForTable(
            normalizedDatasets,
            itemsInOrder,
            do_not_show_zero_or_null_values_in_table,
            hideSingleValueDatasets
          ),
    [
      do_not_show_sum_or_average_row,
      do_not_show_min_max_row,
      itemsInOrder,
      normalizedDatasets,
      do_not_show_zero_or_null_values_in_table,
      hideSingleValueDatasets,
    ]
  )

  const itemsToShowInTable = useMemo(
    () =>
      itemsInOrder.filter(
        (item) => !item.show_only_in_export && datasetsInTable.find((dataset) => dataset.return_id === item.data_id)
      ),
    [itemsInOrder, datasetsInTable]
  )

  const returnIdToDecimals = useMemo(
    () => itemsInOrder.reduce((acc, item) => ({ ...acc, [normalizeDatasetId(item.data_id)]: item.decimals }), {}),
    [itemsInOrder]
  )

  const { sumAndAverageRows, minAndMaxRows } = getFooterAggregationRows(
    datasetsInTable,
    expandableHours ?? false,
    do_not_show_sum_or_average_row,
    do_not_show_min_max_row
  )

  const columnsInTable = useMemo(() => {
    const columns: TimeSeriesDataTableColumnDef[] = (itemsToShowInTable || []).map((item) => {
      const unit = getTypeObject(0, item.data_type, item.unit, item.decimals).unit
      const column: TimeSeriesDataTableColumnDef = {
        enableColumnActions: enableColumnActions,
        accessorKey: serializeTableAccessor(item.data_id),
        header: ``,
        Header: () => <DatasetTableHeader title={item.title} sub_title={item.sub_title} unit={unit} />,
        Cell: LocaleSeparatorCell,
        Footer: () => {
          const key = serializeTableAccessor(item.data_id)
          return (
            <>
              {!do_not_show_sum_or_average_row && (
                <>
                  <span>
                    {sumAndAverageRows[0]?.[key] != null ? addLocaleSeparators(String(sumAndAverageRows[0][key])) : ''}
                  </span>
                  <br />
                  <span>
                    {sumAndAverageRows[1]?.[key] != null ? addLocaleSeparators(String(sumAndAverageRows[1][key])) : ''}
                  </span>
                </>
              )}
              {!do_not_show_min_max_row && (
                <>
                  {!do_not_show_sum_or_average_row && <br />}
                  <span>
                    {minAndMaxRows[0]?.[key] != null ? addLocaleSeparators(String(minAndMaxRows[0][key])) : ''}
                  </span>
                  <br />
                  <span>
                    {minAndMaxRows[1]?.[key] != null ? addLocaleSeparators(String(minAndMaxRows[1][key])) : ''}
                  </span>
                </>
              )}
            </>
          )
        },
        muiTableHeadCellProps: {
          align: 'right',
        },
        minSize: 110,
        size: 110,
        color: item.color ?? null,
        borderRight: item.border_right ?? false,
        align: 'right',
      }

      // Override column properties if they are defined in the columnOverrides prop
      if (columnOverrides && item.data_id in columnOverrides) {
        const relevantOverrides = columnOverrides[item.data_id]
        Object.assign(column, relevantOverrides)
      }

      return column
    })

    const timeColumn: TimeSeriesDataTableColumnDef = {
      enableColumnActions: enableColumnActions,
      accessorKey: `time`,
      header: 'time',
      Header: () => <span style={{ whiteSpace: 'pre-wrap' }}>{i18n.t(`Time`)}</span>,
      Cell: ({ renderedCellValue, row }) => (
        <span
          style={{
            whiteSpace: `nowrap`,
            paddingLeft: row.depth > 0 ? '20px' : '0px',
          }}
          className={styles.DatasetTable_TimeColumn}
        >
          {renderedCellValue}
        </span>
      ),
      Footer: () => {
        return (
          <div className={styles.DatasetTable_TimeColumn}>
            {!do_not_show_sum_or_average_row && (
              <>
                <span>
                  {String(sumAndAverageRows[0][`time`])}{' '}
                  {footer_calculates_per_page ? `(${i18n.t(`current page`)})` : ''}
                </span>
                <br />
                <span>
                  {String(sumAndAverageRows[1][`time`])}{' '}
                  {footer_calculates_per_page ? `(${i18n.t(`current page`)})` : ''}
                </span>
              </>
            )}
            {!do_not_show_min_max_row && (
              <>
                {!do_not_show_sum_or_average_row && <br />}
                <span>
                  {String(minAndMaxRows[0][`time`])} {footer_calculates_per_page ? `(${i18n.t(`current page`)})` : ''}
                </span>
                <br />
                <span>
                  {String(minAndMaxRows[1][`time`])} {footer_calculates_per_page ? `(${i18n.t(`current page`)})` : ''}
                </span>
              </>
            )}
          </div>
        )
      },
      minSize: 75,
      size: footer_calculates_per_page ? 195 : 170,
      align: 'left',
      enableEditing: false,
    }

    // Override column properties if they are defined in the columnOverrides prop
    if (columnOverrides && 'time' in columnOverrides) {
      const relevantOverrides = columnOverrides['time']
      Object.assign(timeColumn, relevantOverrides)
    }

    return [timeColumn, ...(customColumns || []), ...columns]
  }, [
    columnOverrides,
    do_not_show_min_max_row,
    do_not_show_sum_or_average_row,
    enableColumnActions,
    footer_calculates_per_page,
    itemsToShowInTable,
    minAndMaxRows,
    sumAndAverageRows,
    customColumns,
  ])

  const dataInTable: { data: TimeSeriesDataTableData[]; canExpandRows: boolean } = useMemo(() => {
    if (!expandableHours) {
      const { data, canExpandRows } = getHourlyDataForTable(
        datasetsInTable,
        itemsToShowInTable,
        optimizeViewSnap.currentOptimizationHour,
        show_only_value_in_future
      )
      return {
        data: data,
        canExpandRows: canExpandRows,
      }
    } else {
      const { data, canExpandRows } = getQuarterlyDataForTable(
        datasetsInTable,
        itemsToShowInTable,
        optimizeViewSnap.currentOptimizationHour,
        show_only_value_in_future
      )
      return {
        data: data,
        canExpandRows: canExpandRows,
      }
    }
  }, [
    datasetsInTable,
    itemsToShowInTable,
    show_only_value_in_future,
    optimizeViewSnap.currentOptimizationHour,
    expandableHours,
  ])

  const renderTable = useCallback(
    (width: number, height?: number): ReactElement => (
      <div
        style={{
          width,
          height: fillParentHeight ? height : undefined,
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <TimeSeriesDataTable
          columns={columnsInTable}
          data={dataInTable.data}
          useColumnColorFromItem={use_column_color_from_items ?? false}
          initialPageSize={initial_page_size}
          initialPageIndex={initial_page_index}
          hideDownloadButton={do_not_show_download_button}
          datasetsInExport={datasetsInExport}
          showInitialPageWithCurrentOptimizationHour={show_initial_page_with_current_optimization_hour ?? false}
          showInitialPageWithCurrentDay={show_initial_page_with_current_day ?? false}
          datasetNames={datasetNames}
          hideTopToolbar={do_not_show_top_toolbar}
          enableEditing={enableEditing}
          editDisplayMode={enableEditing ? 'table' : undefined}
          tableKey={tableKey}
          tableContainerRef={tableContainerRef}
          disableCustomSorting={disableCustomSorting}
          footerCalculatesPerPage={footer_calculates_per_page ?? false}
          returnIdToDecimals={returnIdToDecimals}
          hideSumAndAverageRow={do_not_show_sum_or_average_row}
          hideMinMaxRow={do_not_show_min_max_row}
          onMouseMove={onMouseMove}
          max_height={max_height}
          sticky_table_header={fillParentHeight || sticky_table_header}
          onPaginationChange={onPaginationChange}
          enableExpanding={expandableHours && dataInTable.canExpandRows}
          onFullScreenClick={onFullScreenClick}
          showCloseButton={showCloseButton}
          onCloseClick={onCloseClick}
          containerHeight={fillParentHeight ? height : undefined}
          enableCellRangeSelectionForClipboard={enableCellRangeSelectionForClipboard ?? false}
        />
      </div>
    ),
    [
      columnsInTable,
      dataInTable.canExpandRows,
      dataInTable.data,
      datasetNames,
      datasetsInExport,
      disableCustomSorting,
      do_not_show_download_button,
      do_not_show_min_max_row,
      do_not_show_sum_or_average_row,
      do_not_show_top_toolbar,
      enableCellRangeSelectionForClipboard,
      enableEditing,
      expandableHours,
      fillParentHeight,
      footer_calculates_per_page,
      initial_page_index,
      initial_page_size,
      max_height,
      onCloseClick,
      onFullScreenClick,
      onMouseMove,
      onPaginationChange,
      returnIdToDecimals,
      showCloseButton,
      show_initial_page_with_current_day,
      show_initial_page_with_current_optimization_hour,
      sticky_table_header,
      tableContainerRef,
      tableKey,
      use_column_color_from_items,
    ]
  )

  return (
    <div
      className={styles.DatasetTable}
      style={{
        maxHeight: max_height,
        height: fillParentHeight ? '100%' : undefined,
        overflow: 'hidden',
        ...(fillParentHeight && { marginTop: 0, marginBottom: 0 }),
      }}
    >
      {title && <div className={styles.DatasetTable_Title}>{title}</div>}
      {fillParentHeight ? (
        <AutoSizer renderProp={({ width, height }) => renderTable(width ?? 0, height ?? 0)} />
      ) : (
        <AutoSizer renderProp={({ width }) => renderTable(width ?? 0)} />
      )}
    </div>
  )
}
